import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/state/authStore.js';
import { useUiStore } from '@/state/uiStore.js';
import NotificationCentre from './NotificationCentre.jsx';
import { redirectToLogout } from '@/api/endpoints/auth.js';
import { apiGet } from '@/api/client.js';

// Set only by the staging frontend workflow (deploy-frontend-staging.yml).
// The live build never sets it, so the badge cannot appear on live even
// though this code is on main. scripts/check-environment-label.mjs fails the
// build if the live workflow ever sets it.
const ENVIRONMENT_LABEL = (import.meta.env.VITE_ENVIRONMENT_LABEL ?? '').trim();
if (ENVIRONMENT_LABEL && typeof document !== 'undefined' && !document.title.startsWith(`[${ENVIRONMENT_LABEL}]`)) {
  document.title = `[${ENVIRONMENT_LABEL}] ${document.title}`;
}

/**
 * Title bar. Visual structure mirrors the monolith
 * (drivemapadmin.html lines 3463–3529):
 *   - Hamburger (mobile sidebar toggle)
 *   - Logo + product name
 *   - Universal search trigger (opens GlobalSearchModal)
 *   - User skeleton → user display + dropdown (Admin Settings, Help, Logout)
 *
 * Admin Settings & Help open per-tab modals that come online in later
 * phases — for now the menu items are wired but their click handlers
 * are placeholders that route via uiStore.openConfirm("Coming soon").
 *
 * The `userDisplayNew` / `userAvatarNew` / `userNameNew` / `userRoleNew`
 * ids are preserved so any extracted CSS selectors that target them
 * continue to apply.
 */

/** Default avatar SVG (data URI) — used when no photoUrl is available. */
const DEFAULT_AVATAR =
  'data:image/svg+xml;base64,' +
  btoa(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">' +
      '<rect width="40" height="40" rx="20" fill="#667eea"/>' +
      '<text x="20" y="26" font-family="Segoe UI, sans-serif" font-size="16" font-weight="600" text-anchor="middle" fill="#fff">U</text>' +
      '</svg>'
  );

/**
 * @param {{ onOpenGlobalSearch: () => void, onOpenAdminSettings: () => void, onOpenHelp: () => void }} props
 */
export default function TitleBar({ onOpenGlobalSearch, onOpenAdminSettings, onOpenHelp }) {
  const user = useAuthStore((s) => s.user);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const authLoading = useAuthStore((s) => s.loading);
  const initialised = useAuthStore((s) => s.initialised);

  const setSidebarMobileOpen = useUiStore((s) => s.setSidebarMobileOpen);
  const sidebarMobileOpen = useUiStore((s) => s.sidebarMobileOpen);
  const setActiveTab = useUiStore((s) => s.setActiveTab);
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);

  const canSeeSecurity = useAuthStore((s) => s.hasPermission('security-dashboard'));

  const canSeeNetwork = useAuthStore((s) => s.hasPermission('network-dashboard'));

  // Forcepoint web-filtering blocks share the security-dashboard permission.

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [avatarSrc, setAvatarSrc] = useState(/** @type {string | null} */ (null));

  // Reset avatar whenever the user changes. Prefer an explicit photoUrl;
  // otherwise fetch the live Graph photo via the authenticated JSON
  // endpoint (GET /api/ad/user-photo?upn=) — matching the pattern used by
  // the user-detail avatar (UserDetailModal.jsx's UserAvatar). A bare
  // <img src="/api/..."> does NOT work here: every /api/* route requires a
  // Bearer token (see middleware/auth.ts requireAuth), and a plain <img>
  // tag cannot attach an Authorization header, so it always 401s. There is
  // also no `/api/user-photo/:id` route — only the UPN-keyed
  // `/api/ad/user-photo` — so resolve via user.upn, not user.id.
  useEffect(() => {
    if (user?.photoUrl) {
      setAvatarSrc(user.photoUrl);
      return undefined;
    }
    if (!user?.upn) {
      setAvatarSrc(null);
      return undefined;
    }
    let cancelled = false;
    setAvatarSrc(null);
    apiGet(`/api/ad/user-photo?upn=${encodeURIComponent(user.upn)}`)
      .then((data) => { if (!cancelled && data?.photo) setAvatarSrc(data.photo); })
      .catch(() => { /* a real failure; no photo now returns 200 with null */ });
    return () => { cancelled = true; };
  }, [user]);

  // Close dropdown on outside click or Escape.
  useEffect(() => {
    if (!dropdownOpen) return undefined;
    /** @param {MouseEvent} e */
    function onDocClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(/** @type {Node} */ (e.target))) {
        setDropdownOpen(false);
      }
    }
    /** @param {KeyboardEvent} e */
    function onKey(e) {
      if (e.key === 'Escape') setDropdownOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [dropdownOpen]);

  // Ctrl+K opens global search (matches the monolith's shortcut chip).
  useEffect(() => {
    /** @param {KeyboardEvent} e */
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenGlobalSearch();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onOpenGlobalSearch]);

  const showSkeleton = !initialised || authLoading || !user;

  return (
    <header className="title-bar">
      <div className="title-bar-left">
        <button
          className="hamburger-btn"
          id="hamburgerBtn"
          onClick={() => setSidebarMobileOpen(!sidebarMobileOpen)}
          aria-label="Toggle navigation"
          type="button"
        >
          <i className="fas fa-bars" aria-hidden="true" />
        </button>
        <div className="title-bar-logo">
          <img src="/images/BVH_Circle.png" alt="BVH" className="logo-img" />
          <span className="logo-text">Omni Cloud</span>
          {ENVIRONMENT_LABEL && (
            <span
              className="env-badge env-badge-test"
              title="This is the staging copy of Omni Cloud. It uses live data."
            >
              {ENVIRONMENT_LABEL}
            </span>
          )}
        </div>
      </div>

      <div className="title-bar-center">
        <button
          type="button"
          className="universal-search"
          onClick={onOpenGlobalSearch}
          aria-label="Open quick search"
        >
          <i className="fas fa-search search-icon" aria-hidden="true" />
          <span className="search-placeholder">Search users, groups, drives, printers...</span>
          <kbd className="search-shortcut">Ctrl+K</kbd>
        </button>
      </div>

      <div className="title-bar-right">
        <button
          type="button"
          onClick={() => toggleTheme()}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            background: 'transparent', border: 'none', color: '#94a3b8',
            fontSize: 18, width: 38, height: 38, padding: 0, cursor: 'pointer', borderRadius: 8,
            marginRight: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseOut={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'transparent'; }}
        >
          <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'}`} aria-hidden="true" />
        </button>
        {/* Notification Centre — durable, server-backed, and now the ONLY
            place notifications appear. */}
        <NotificationCentre />
        {/* The three feature bells (Network, Security, Web Filtering) lived
            here. They are gone: those feeds now write to the Notification
            Centre above, which records them durably and shows them to
            everyone who should see them rather than only to whoever happened
            to have a tab open. See
            docs/notification-centre-consolidation-plan.md. */}
        <div id="userDisplayAreaNew">
          {showSkeleton ? (
            <div className="user-display-skeleton" id="userSkeletonNew">
              <div className="skeleton-avatar" />
              <div className="user-info">
                <div className="skeleton-text skeleton-name" />
                <div className="skeleton-text skeleton-role" />
              </div>
            </div>
          ) : (
            <div
              className="user-display"
              id="userDisplayNew"
              ref={dropdownRef}
              style={{ position: 'relative' }}
            >
              <div
                onClick={() => setDropdownOpen((v) => !v)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                  background: 'rgba(255,255,255,0.05)'
                }}
                role="button"
                tabIndex={0}
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDropdownOpen((v) => !v);
                  }
                }}
              >
                <img
                  id="userAvatarNew"
                  className="user-avatar"
                  src={avatarSrc ?? DEFAULT_AVATAR}
                  alt={user.displayName ? `${user.displayName} avatar` : 'User avatar'}
                  onError={() => setAvatarSrc(DEFAULT_AVATAR)}
                />
                <div className="user-info">
                  <div className="user-name" id="userNameNew">
                    {user.displayName || 'Unknown User'}
                  </div>
                  <div className="user-role" id="userRoleNew">
                    {user.role || 'user'}
                  </div>
                </div>
                <svg
                  className="settings-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>

              {dropdownOpen && (
                <div
                  id="userDropdownNew"
                  role="menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    border: '1px solid var(--hairline-strong)',
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(var(--shadow-rgb),0.2)',
                    minWidth: '240px',
                    marginTop: '8px',
                    zIndex: 10000,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}
                >
                  {isAdmin && (
                    <button
                      type="button"
                      role="menuitem"
                      id="adminSettingsBtnNew"
                      onClick={() => {
                        setDropdownOpen(false);
                        onOpenAdminSettings();
                      }}
                      style={{
                        width: '100%',
                        padding: '14px 20px',
                        background: 'none',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px'
                      }}
                    >
                      <i className="fas fa-cog" style={{ color: '#667eea', fontSize: '16px', width: '20px' }} aria-hidden="true" />
                      <span style={{ fontWeight: 500, fontSize: '14px' }}>Admin Settings</span>
                    </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    id="themeToggleBtnNew"
                    onClick={() => toggleTheme()}
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px'
                    }}
                  >
                    <i
                      className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'}`}
                      style={{ color: '#667eea', fontSize: '16px', width: '20px' }}
                      aria-hidden="true"
                    />
                    <span style={{ fontWeight: 500, fontSize: '14px' }}>
                      {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                    </span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setDropdownOpen(false);
                      onOpenHelp();
                    }}
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px'
                    }}
                  >
                    <i className="fas fa-question-circle" style={{ color: '#667eea', fontSize: '16px', width: '20px' }} aria-hidden="true" />
                    <span style={{ fontWeight: 500, fontSize: '14px' }}>Help Center</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setDropdownOpen(false);
                      redirectToLogout();
                    }}
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px'
                    }}
                  >
                    <i className="fas fa-sign-out-alt" style={{ color: '#ef4444', fontSize: '16px', width: '20px' }} aria-hidden="true" />
                    <span style={{ fontWeight: 500, fontSize: '14px' }}>Logout</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
